import React from 'react';
import { FadeInUp, SectionLabelLight, AccentLineLight, StaggerContainer, StaggerItem } from './AnimationWrappers';
import { Shield, Layers, Zap, Scissors, Wind, Thermometer, LayoutGrid } from 'lucide-react';

const CAPABILITIES = [
  { icon: Shield, num: '01', title: 'Seam Sealing', desc: 'Hot-air welded PU seam tape at 420°C for zero-leak construction across all waterproof garments.' },
  { icon: Layers, num: '02', title: 'Sew Free Bonded Assembly', desc: 'Thermally bonded panels eliminate needle penetration. No holes. No weak points. Pure structural integrity.' },
  { icon: Scissors, num: '03', title: 'Laser Cutting & Perforation', desc: 'CO₂ laser systems for ±0.1mm precision cutting and micro-perforation ventilation patterns.' },
  { icon: Zap, num: '04', title: 'Ultrasonic Welding', desc: 'High-frequency vibration bonding for fabrics. Clean edges, zero fraying, maximum durability.' },
  { icon: Wind, num: '05', title: 'Down Filling', desc: 'Automated filling systems with gram-accurate distribution. Enclosed chambers prevent fill loss while ensuring uniform insulation density.' },
  { icon: LayoutGrid, num: '06', title: 'Quilting', desc: 'Specialized machines for baffle-box and stitch-through construction. Programmable patterns ensure uniform stitch density across all insulated garments.' },
  { icon: Thermometer, num: '07', title: 'Thermal Transfer & Lamination', desc: 'Heat-press fusing with ±2°C control for interlinings, reflective transfers, and laminated patches.' },
];

const CapabilitiesSection = () => {
  return (
    <section id="capabilities" data-testid="capabilities-section" className="relative overflow-hidden" style={{ background: '#1C1C1E', padding: '40px 0 30px' }}>
      <div className="container-main relative z-10">
        <SectionLabelLight number="04" title="Capabilities" />
        <FadeInUp>
          <AccentLineLight />
          <h2 className="font-display text-white mb-2 md:mb-3" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.4rem)', fontWeight: 700 }}>
            Beyond cut <span style={{ color: '#C8102E' }}>and sew.</span>
          </h2>
          <p className="font-body mb-6 md:mb-12" style={{ fontSize: '0.85rem', fontWeight: 300, color: 'rgba(255,255,255,0.6)', maxWidth: '580px' }}>
            Seven in-house controlled processes that give us full command over every stitch, bond, and seal.
          </p>
        </FadeInUp>

        {/* Mobile: compact 2-col grid */}
        <div className="md:hidden grid grid-cols-2 gap-2">
          {CAPABILITIES.map((cap, i) => (
            <div
              key={i}
              data-testid={`capability-${i}`}
              className="relative p-3"
              style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px' }}
            >
              <div className="absolute top-0 left-0 w-[24px] h-[2px]" style={{ background: '#C8102E' }} />
              <cap.icon size={16} color="#C8102E" className="mb-2" />
              <h3 className="font-display text-white" style={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.3 }}>{cap.title}</h3>
            </div>
          ))}
        </div>

        {/* Desktop: full card grid */}
        <StaggerContainer className="hidden md:grid md:grid-cols-2 lg:grid-cols-3" style={{ gap: '1px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
          {CAPABILITIES.map((cap, i) => (
            <StaggerItem key={i}>
              <div
                data-testid={`capability-desktop-${i}`}
                className="card-dark relative group"
                style={{ padding: '1.4rem 1.5rem' }}
              >
                <div className="absolute top-0 left-0 w-[40px] h-[3px]" style={{ background: '#C8102E' }} />
                <span className="font-mono block mb-4" style={{ fontSize: '0.67rem', color: 'rgba(255,255,255,0.35)' }}>{cap.num}</span>
                <h3 className="font-display text-white mb-3" style={{ fontSize: '1.1rem', fontWeight: 600 }}>{cap.title}</h3>
                <p className="font-body" style={{ fontSize: '0.85rem', fontWeight: 300, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>{cap.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
      <div className="section-number" style={{ color: 'rgba(255,255,255,0.03)' }}>04</div>
    </section>
  );
};

export default CapabilitiesSection;
